import React from 'react';
import {
    Button,
    Progress,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes } from '../Common';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
    LinkCellRenderer,
    IconCellRenderer,
    VariableLinkCellRenderer,
    TextFilterDefaults,
    DateFilterDefaults,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    SmallButton,
    PageHeading,
    PageWrap,
    onFieldChange,
    toasty,
} from '../common/forms/FormElements';
import { handleFormSaveError } from '../common/forms/ValidationError';
import CustomCircularProgress from '../common/CustomCircularProgress';
import { NotAuthorizedPage } from '../status/StatusCodes';
import SlideForm from '../common/forms/SlideForm';
import BillingIssuesWidget from '../widgets/BillingIssuesWidget';
import { quickSaleStatusOptions } from './QuickSale';
import QuickSaleSlideout from './QuickSaleSlideout';

class QuickSalesIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.billingIssuesSlideoutRef = React.createRef();
        this.quickSaleSlideoutRef = React.createRef();

        this.state = {
            loading: true,
            rowData: [],
            rowsSelected: [],
            selectedDetails: [],
            selectedRowRejectionNotes: '',
            showBillingIssuesSlideout: false,
            billingIssueMessages: [],
            showQuickSaleSlideout: false,
        };

        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onBillingIssuesSlideout = async (id) => {
        const messages = await httpClient.js(
            ApiRoutes.billingIssues.messages(id),
        );

        this.setState({
            billingIssueMessages: messages,
            showBillingIssuesSlideout: true,
        });
    };

    onChange = onFieldChange;

    onRowSelected = (e) => {
        const rs = e.api.getSelectedRows();

        this.setState({
            rowsSelected: rs,
        });
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    populateState = () => {
        const initialListFilters = {
            initialDispatchId: '',
            initialCustomerCompanyId: 0,
            initialOperationsCenterId: 0,
            initialBillingStatusId: 6,
            initialPayrollStatusId: 0,
            initialHasOverride: null,
        };

        const billingStatuses = quickSaleStatusOptions;

        const gridOptions = createGridOptions(this);
        const context = this;

        gridOptions.rowSelection = 'single';

        gridOptions.postProcessData = this.transformRowData;
        gridOptions.onRowSelected = this.onRowSelected;
        gridOptions.rowClassRules = {};

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
            iconRenderer: IconCellRenderer,
            variableLinkRenderer: VariableLinkCellRenderer,
        };

        const billingStatusFilterParams = {
            suppressFilterButton: true,
            options: billingStatuses,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: initialListFilters.initialBillingStatusId,
        };

        gridOptions.columnDefs = [
            {
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'Date',
                headerName: 'Date',
                field: 'date',
                sortable: true,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            },
            {
                colId: 'CustomerName',
                headerName: 'Customer Name',
                field: 'customerName',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'ContractNumber',
                headerName: 'Contract #',
                field: 'contractNumber',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Timesheet.TimesheetNumber',
                headerName: 'Quick Sale #',
                field: 'timesheetNumber',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Id',
                flex: 0,
                maxWidth: 100,
                headerName: 'View',
                sortable: false,
                cellRenderer: (params) => {
                    if (params.data) {
                        return (
                            <SmallButton
                                type="button"
                                onClick={() => {
                                    this.openQuickSaleSlideout(params.data.id);
                                }}
                            >
                                <i className="fa fa-eye fa-md mr-2" />
                            </SmallButton>
                        );
                    }
                    return null;
                },
            },
            {
                colId: 'BillingStatusId',
                headerName: 'Status',
                field: 'billingStatus',
                sortable: true,
                flex: 1,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: billingStatusFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: billingStatusFilterParams,
            },
            {
                colId: 'Id',
                flex: 2,
                maxWidth: 150,
                headerName: 'Billing Issue',
                sortable: false,
                cellRenderer: (params) => {
                    if (params.data) {
                        return (
                            <SmallButton
                                title="Billing Issue"
                                type="button"
                                onClick={() => {
                                    this.onBillingIssuesSlideout(
                                        params.data.timesheetId,
                                    );
                                }}
                            >
                                <i className="fa fa-comments fa-md mr-2" />
                            </SmallButton>
                        );
                    }

                    return null;
                },
            },
        ];

        gridOptions.onFilterChanged = () => { };

        gridOptions.onSortChanged = () => { };

        gridOptions.onFirstDataRendered = () => { };

        gridOptions.onGridReady = (params) => {
            params.api.setDatasource(context.state.dataSource);
            context.setState({ gridApi: params.api });
        };

        const dataSource = createDataSource(
            ApiRoutes.quickSale.search(),
            gridOptions,
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
            isRejectingTimesheet: false,
        });
    };

    onAddQuickSale = async () => {
        await this.context.setFormOpened(true);
        this.setState({ showQuickSaleSlideout: true });
        this.quickSaleSlideoutRef.current.open();
    };

    openQuickSaleSlideout = async (id) => {
        await this.context.setFormOpened(true);
        this.setState({ showQuickSaleSlideout: true });
        if (id) {
            const model = {
                id,
            };
            this.quickSaleSlideoutRef.current.open(model);
        } else {
            this.quickSaleSlideoutRef.current.open();
        }
    };

    onReplyToBillingIssues = async (id, message, updateStatus) => {
        if (message == null || message.length == 0) {
            toasty.error('Please supply reply notes');
            return;
        }

        const model = {
            timesheetId: id,
            notes: message,
            updateStatus,
        };

        await httpClient.post(ApiRoutes.billingIssues.reply(), model);

        toasty.success('Billing issue reply successful');

        this.setState({
            showBillingIssuesSlideout: false,
        });

        this.state.gridOptions.refresh();
    };

    onQuickSaleSlideoutClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ rowsSelected: [] });
        this.state.gridOptions.refresh();
    };

    render() {
        const {
            rowData,
            gridOptions,
            selectedRow,
            billingIssueMessages,
            loading,
        } = this.state;

        const { tenantSettings } = this.context.tenant;
        if (loading || !tenantSettings) {
            return (
                <Progress />
            );
        }

        if (
            !tenantSettings.billingEnabled || !tenantSettings.invoicingEnabled || !tenantSettings.enableQuickSales
        ) return <NotAuthorizedPage />;

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faComments}
                        className="mr-2 text-muted"
                    />
                    <span>Quick Sales</span>
                    <span
                        style={{
                            float: 'right',
                            position: 'relative',
                            top: '-5px',
                        }}
                    >
                        <Button
                            size="sm"
                            color="success"
                            style={{
                                marginRight: '10px',
                            }}
                            onClick={() => this.onAddQuickSale()}
                        >
                            Add Quick Sale
                        </Button>
                    </span>
                </PageHeading>

                <DataGridToolbar
                    entity="BillingIssues"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    hideAdd
                    hideExcelButton
                    gridOptions={this.state.gridOptions}
                />
                {!!this.state.loadingReport && (
                    <CustomCircularProgress />
                )}

                {!this.state.loadingReport && (
                    <DataGrid
                        domLayout="normal"
                        rowData={rowData}
                        gridOptions={gridOptions}
                        gridStatus={this.state.gridStatus}
                    />
                )}
                <SlideForm
                    loading={false}
                    show={this.state.showBillingIssuesSlideout}
                    id="billingIssuesSlideout"
                    formIcon={faComments}
                    formTitle="Billing Issues"
                    onClose={() => {
                        this.setState({
                            showBillingIssuesSlideout: false,
                        });
                    }}
                >
                    <BillingIssuesWidget
                        ref={this.billingIssuesSlideoutRef}
                        billing={selectedRow}
                        messages={billingIssueMessages}
                        context="replying"
                        onReplyCallback={(
                            id,
                            message,
                            updateStatus,
                        ) => {
                            this.onReplyToBillingIssues(
                                id,
                                message,
                                updateStatus,
                            );
                        }}
                        onCloseCallback={() => {
                            this.setState({
                                showBillingIssuesSlideout: false,
                            });
                        }}
                    />
                </SlideForm>
                <QuickSaleSlideout
                    ref={this.quickSaleSlideoutRef}
                    show={this.state.showQuickSaleSlideout}
                    toggleShow={(open) => this.setState({ showQuickSaleSlideout: open })}
                    onClose={this.onQuickSaleSlideoutClosed}
                >
                </QuickSaleSlideout>
            </PageWrap>
        );
    }
}

export default QuickSalesIndex;
